import { useGetIdentity } from "@refinedev/core";
import { Card, Typography, Row, Col, Space, Button, Empty, theme } from "antd";
import {
  AppstoreOutlined,
  SettingOutlined,
  RocketOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useResponsive } from "../../hooks";
import { APP_CONFIG } from "../../config/app";
import { PageTourButton } from "../../features/userGuide";

const { Title, Text, Paragraph } = Typography;

export const PlaceholderDashboard: React.FC = () => {
  const { data: identity } = useGetIdentity<{ name: string }>();
  const { t } = useTranslation();
  const { isMobile, isTablet } = useResponsive();
  const { token } = theme.useToken();

  const pagePadding = isMobile ? 12 : isTablet ? 16 : 24;
  const gutter: [number, number] = isMobile ? [8, 8] : [16, 16];

  return (
    <div
      style={{
        padding: pagePadding,
        background: token.colorBgLayout,
        minHeight: "100vh",
      }}
    >
      {/* Welcome Header */}
      <Row
        justify="space-between"
        align={isMobile ? "top" : "middle"}
        style={{ marginBottom: isMobile ? 16 : 24 }}
        gutter={[0, 12]}
      >
        <Col xs={24}>
          <Space align="center">
            <Title level={isMobile ? 4 : 3} style={{ margin: 0 }}>
              {t("dashboard.welcomeBack", { name: identity?.name || "User" })}
            </Title>
            <PageTourButton tourId="dashboard-overview" size="small" iconOnly />
          </Space>
          <div>
            <Text type="secondary">{APP_CONFIG.appName}</Text>
          </div>
        </Col>
      </Row>

      {/* Placeholder Content */}
      <Row gutter={gutter} data-tour="kpi-cards">
        <Col xs={24}>
          <Card
            data-tour="charts"
            style={{
              textAlign: "center",
              minHeight: 400,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Empty
              image={
                <AppstoreOutlined
                  style={{ fontSize: 64, color: token.colorPrimary, opacity: 0.5 }}
                />
              }
              description={
                <Space direction="vertical" size="large">
                  <Title level={4} style={{ margin: 0, color: token.colorTextSecondary }}>
                    {t("dashboard.placeholder.title", "Dashboard Coming Soon")}
                  </Title>
                  <Paragraph
                    type="secondary"
                    style={{ maxWidth: 400, margin: "0 auto" }}
                  >
                    {t(
                      "dashboard.placeholder.description",
                      "This is a placeholder dashboard. Configure VITE_DASHBOARD_TYPE environment variable to enable specific dashboard modules.",
                    )}
                  </Paragraph>
                </Space>
              }
            >
              <Space size="middle" wrap>
                <Button icon={<SettingOutlined />} disabled>
                  {t("dashboard.placeholder.configure", "Configure")}
                </Button>
                <Button type="primary" icon={<RocketOutlined />} disabled>
                  {t("dashboard.placeholder.getStarted", "Get Started")}
                </Button>
              </Space>
            </Empty>
          </Card>
        </Col>
      </Row>

      {/* Feature Cards */}
      <Row
        gutter={gutter}
        style={{ marginTop: isMobile ? 16 : 24 }}
        data-tour="recent-items"
      >
        <Col xs={24} md={8}>
          <Card
            hoverable
            style={{ textAlign: "center", opacity: 0.6 }}
            data-tour="pending-tasks"
          >
            <Space direction="vertical">
              <AppstoreOutlined style={{ fontSize: 32, color: token.colorPrimary }} />
              <Text strong>
                {t("dashboard.placeholder.feature1", "Feature 1")}
              </Text>
              <Text type="secondary">
                {t(
                  "dashboard.placeholder.feature1Desc",
                  "Description for feature 1",
                )}
              </Text>
            </Space>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card hoverable style={{ textAlign: "center", opacity: 0.6 }}>
            <Space direction="vertical">
              <SettingOutlined style={{ fontSize: 32, color: token.colorSuccess }} />
              <Text strong>
                {t("dashboard.placeholder.feature2", "Feature 2")}
              </Text>
              <Text type="secondary">
                {t(
                  "dashboard.placeholder.feature2Desc",
                  "Description for feature 2",
                )}
              </Text>
            </Space>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card hoverable style={{ textAlign: "center", opacity: 0.6 }}>
            <Space direction="vertical">
              <RocketOutlined style={{ fontSize: 32, color: token.colorWarning }} />
              <Text strong>
                {t("dashboard.placeholder.feature3", "Feature 3")}
              </Text>
              <Text type="secondary">
                {t(
                  "dashboard.placeholder.feature3Desc",
                  "Description for feature 3",
                )}
              </Text>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};
